import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SearchBar } from '../SearchBar'
import { AccountSwitcher } from '../AccountSwitcher'
import { useAuthStore } from '../../auth/useAuthStore'
import { usePortfolioSummary } from '../../hooks/usePortfolioSummary'
import { useMaskMoney } from '../../privacy/usePrivacyStore'

function fmtCompactUsd(n: number): string {
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function fmtSignedPct(change: number, base: number): string {
  if (!Number.isFinite(change) || base === 0) return '—'
  const pct = (change / base) * 100
  const sign = pct >= 0 ? '+' : '−'
  return `${sign}${Math.abs(pct).toFixed(2)}%`
}

interface TopBarProps {
  portfolioOpen?: boolean
  onTogglePortfolio?: () => void
  sidebarCollapsed?: boolean
}

export function TopBar({ portfolioOpen = false, onTogglePortfolio, sidebarCollapsed = false }: TopBarProps = {}) {
  const navigate = useNavigate()
  const setAccountId = useAuthStore((s) => s.setAccountId)
  const { portfolioValue, dayChange } = usePortfolioSummary()
  const mask = useMaskMoney()

  const dayBase = portfolioValue - dayChange
  const positive = dayChange >= 0
  const arrow = positive ? '▲' : '▼'
  const dayColor = positive ? 'text-emerald-400' : 'text-red-400'

  // Mirror the logo's direction in the browser tab favicon. Same SVG paths as
  // the inline logo below, recolored to emerald-400 / red-400.
  useEffect(() => {
    const stroke = positive ? '#34d399' : '#f87171'
    const paths = positive
      ? '<path d="M3 17 L9 11 L13 15 L21 7"/><path d="M15 7 L21 7 L21 13"/>'
      : '<path d="M3 7 L9 13 L13 9 L21 17"/><path d="M15 17 L21 17 L21 11"/>'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`
    const href = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.type = 'image/svg+xml'
    link.href = href
  }, [positive])

  return (
    <header className="row-start-1 col-span-full border-b border-zinc-800 bg-white/[0.02] flex items-stretch">
      <Link
        to="/"
        aria-label="Home"
        className={`flex items-center justify-center shrink-0 ${
          sidebarCollapsed ? 'w-[3.5rem]' : 'w-48'
        } ${positive ? 'text-emerald-400' : 'text-red-400'}`}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-5 h-5"
          aria-hidden="true"
        >
          {positive ? (
            <>
              <path d="M3 17 L9 11 L13 15 L21 7" />
              <path d="M15 7 L21 7 L21 13" />
            </>
          ) : (
            <>
              <path d="M3 7 L9 13 L13 9 L21 17" />
              <path d="M15 17 L21 17 L21 11" />
            </>
          )}
        </svg>
      </Link>

      <div className="flex-1 min-w-0 max-w-xl border-l border-zinc-800">
        <SearchBar variant="header" onSelect={(symbol) => navigate(`/instruments/${symbol}`)} />
      </div>

      <div className="flex items-stretch ml-auto">
        <button
          type="button"
          onClick={onTogglePortfolio}
          aria-pressed={portfolioOpen}
          aria-label={portfolioOpen ? 'Hide portfolio panel' : 'Show portfolio panel'}
          title={portfolioOpen ? 'Hide portfolio panel' : 'Show portfolio panel'}
          className={`font-mono text-sm hidden md:flex items-center gap-2 px-4 border-l border-zinc-800 transition hover:bg-white/[0.04] ${
            portfolioOpen ? 'bg-white/[0.04]' : ''
          }`}
        >
          <span className="text-zinc-100">{mask(fmtCompactUsd(portfolioValue))}</span>
          <span className={dayColor}>
            {arrow} {fmtSignedPct(dayChange, dayBase)}
          </span>
        </button>
        <AccountSwitcher variant="header" onChange={setAccountId} />
      </div>
    </header>
  )
}
